import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Instagram } from "lucide-react";
import { useRef } from "react";
import { Navbar } from "@/components/Navbar";
import { RevealLine, ScrollReveal } from "@/components/Reveal";

const DRIVE = "https://lh3.googleusercontent.com/d/";
const WREN_IMG = `${DRIVE}1_bAtw-ajtihv543PqImTUyr_HX67qd9e=w1600`;
const BIRDHOUSE_IMG = `${DRIVE}1n7WXef9vv6ek1WXMd3o-60cf0mnNwGc_=w1400`;
const LAKE_IMG = `${DRIVE}1J_JezagsyC1uS8W0mjXByunDyBTL69oP=w2000`;
const PROJECT_URL = "https://script.google.com/macros/s/AKfycbzD3gghMNhlwzAZGUPwHALwbPR-SBzxVNjRQZ9UM2OE7298q3AYM38FLPoRhDCvZUl5TA/exec";

const FloatCard = ({ title, children, className = "", delay = 0, testId }) => (
  <ScrollReveal delay={delay} className={className}>
    <div className="border border-border bg-background/95 p-8 shadow-[0_20px_60px_-20px_rgba(20,40,25,0.35)] backdrop-blur-sm" data-testid={testId}>
      <h3 className="font-serif text-2xl font-semibold tracking-tight underline decoration-primary decoration-2 underline-offset-8">
        {title}
      </h3>
      <div className="mt-5 text-sm leading-relaxed text-foreground/80">{children}</div>
    </div>
  </ScrollReveal>
);

export default function GetInvolved() {
  const lakeRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: lakeRef, offset: ["start end", "end start"] });
  const lakeY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div className="min-h-screen" data-testid="get-involved-page">
      <Navbar />

      {/* Split hero: wren photo + dark title panel */}
      <section className="grid min-h-[85vh] pt-[68px] md:grid-cols-2" data-testid="gi-hero">
        <motion.div
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative overflow-hidden"
        >
          <img
            src={WREN_IMG}
            alt="A northern house wren carrying a twig into a student-built bird box"
            className="h-64 w-full object-cover md:absolute md:inset-0 md:h-full"
          />
          <p className="absolute bottom-0 left-0 hidden bg-background/85 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] backdrop-blur-md md:block">
            A house wren moving into a student-built box
          </p>
        </motion.div>
        <div className="flex items-center bg-foreground px-8 py-16 text-background md:px-16">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-xs font-bold uppercase tracking-[0.35em] text-background/60"
              data-testid="gi-eyebrow"
            >
              CK Finding Fauna · Conservation
            </motion.p>
            <h1 className="mt-8 font-serif text-5xl font-semibold leading-[0.95] tracking-tighter md:text-7xl" data-testid="gi-title">
              <RevealLine delay={0.3}>Getting Involved:</RevealLine>
              <RevealLine delay={0.45} className="italic text-primary">Lake Jonah</RevealLine>
              <RevealLine delay={0.6}>Project</RevealLine>
            </h1>
          </div>
        </div>
      </section>

      {/* Framed intro statement */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-16 md:py-32">
        <ScrollReveal>
          <div className="border-2 border-foreground p-8 md:p-14" data-testid="gi-intro">
            <p className="mx-auto max-w-3xl text-center font-serif text-xl leading-relaxed md:text-2xl">
              Lake Jonah is one of two major bodies of water on CK campus. Unlike Kingswood Lake, Lake Jonah was man-made and used to be a pool! In 1999, the pool closed, and it has sat for the last 20 years undisturbed. Now it hosts various species of frogs and fish — however, due to run-off and invasive species, many native species in Michigan have been killed or pushed off of the water and surrounding area. As a community, we have a duty to take care of our environment, especially spaces we created.
            </p>
          </div>
        </ScrollReveal>
      </section>

      {/* The project: text + birdhouse photo */}
      <section className="mx-auto grid max-w-7xl items-center gap-12 px-6 pb-24 md:grid-cols-2 md:px-16 md:pb-32">
        <ScrollReveal>
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-primary">01 — The mission</p>
          <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight md:text-5xl" data-testid="gi-project-heading">
            The Lake Jonah Project
          </h2>
          <p className="mt-6 max-w-md text-base leading-loose text-foreground/80">
            The Lake Jonah Project aims to restore Lake Jonah's environment. Through building bird boxes, planting native foliage, and introducing native fish to eat invasive goldfish, students can get involved in a hands-on conservation project on campus.
          </p>
        </ScrollReveal>
        <ScrollReveal delay={0.15}>
          <div className="group overflow-hidden border border-border" data-testid="gi-birdhouse-photo">
            <img
              src={BIRDHOUSE_IMG}
              alt="A bird box built by students for the Lake Jonah Project"
              className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
          </div>
        </ScrollReveal>
      </section>

      {/* Full-bleed lake photo with floating cards */}
      <section ref={lakeRef} className="relative overflow-hidden" data-testid="gi-lake-section">
        <motion.img
          style={{ y: lakeY }}
          src={LAKE_IMG}
          alt="The Lake Jonah environment today"
          className="absolute inset-0 h-[116%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-foreground/25" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 md:px-16 md:py-36">
          <div className="grid gap-8 md:grid-cols-2">
            <FloatCard title="How to Get Involved" testId="gi-how-card">
              Through Bee Club's conservation sector, or through your own contributions to citizen science, you can get involved in helping restore CK's natural habitats! Look out for Bee Club invasive-species pulling ventures, check for native foliage plantings, or go for a walk on your own and document what plant life you locate around campus to let us know what is in the area!
            </FloatCard>
            <FloatCard title="Lake Jonah Project Website" delay={0.12} testId="gi-website-card">
              Follow the link on this page to the Lake Jonah Project website. There you will see pages for logging native plants, invasive removal, carbon dioxide consumption by trees on campus, and a timeline for all logs. To start identifying native plants on campus, download any of these free apps — iNaturalist, PictureThis, or PlantNet — and identify native plants via photos!
            </FloatCard>
            <FloatCard title="Citizen Science" delay={0.1} testId="gi-citizen-card">
              Citizen science is the process of citizens — non-scientists — helping with data collection for research. As a community, we can participate by going on quick walks and identifying one or two plants! This way we can log what time of year plants grow, where they are located, and the survival status of native species competing against invasives — and focus our energy where the data says it matters.
            </FloatCard>
            <ScrollReveal delay={0.2} className="flex items-center justify-center">
              <a
                href={PROJECT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gi-project-link"
                className="group flex flex-col items-center gap-4 rounded-full border-2 border-background bg-background px-12 py-14 text-center shadow-[0_20px_60px_-20px_rgba(20,40,25,0.45)] transition-transform duration-500 hover:-translate-y-2"
              >
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">Get involved today</span>
                <span className="flex items-center gap-2 font-serif text-2xl font-semibold tracking-tight">
                  Lake Jonah Project Website
                  <ArrowUpRight className="h-5 w-5 text-primary transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </a>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Links strip */}
      <footer className="border-t border-border bg-foreground text-background" data-testid="gi-footer">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-6 py-10 md:px-16">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-background/50">Links</p>
          <a
            href="https://instagram.com/ck_findingfauna"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gi-instagram-link"
            className="group flex items-center gap-2 text-sm transition-colors duration-300 hover:text-primary"
          >
            <Instagram className="h-4 w-4" />
            @ck_findingfauna
          </a>
        </div>
      </footer>
    </div>
  );
}
